/**
 * optimise-ui.ts — the optimiser panel (P1 US-115).
 *
 * Ported from oracle lines 1128–1155 (the results table and the "appliquer" handler) and 1247–1249
 * (the family checkboxes). The sweep itself now runs on the server (US-109, US-110); this file
 * only sends the request and renders what comes back.
 */

import type { OptimiseResponse } from '@ronceray/contract';

import { optimiseRequest, type ApiResult } from './api-client';
import { $, $$, must } from './dom';
import { fmt, ratio3 } from './format';
import type { FicheModule } from './fiches';

/** The seven designation families, all checked by default. Oracle lines 1247–1249. */
export function familyCheckboxesHTML(familles: readonly string[]): string {
  return familles
    .map((f) => `<label><input type="checkbox" value="${f}" checked> ${f}</label>`)
    .join('');
}

/** Which families are ticked. Empty means "all", which the contract and the sweep both honour. */
export function selectedFamilies(): string[] {
  return $$<HTMLInputElement>('#optfam input:checked').map((input) => input.value);
}

/** The timber sweep scope from `#optbois`. */
export function selectedBoisMode(): 'actuelle' | 'massif' | 'gl' {
  const value = $<HTMLSelectElement>('#optbois')?.value;
  return value === 'massif' || value === 'gl' ? value : 'actuelle';
}

/** The oracle's exact empty-state sentence, line 1129. */
export const EMPTY_SWEEP_MESSAGE =
  'Aucune section de la base ne satisfait toutes les verifications avec ces sollicitations.';

/**
 * Render the candidate table. Oracle lines 1130–1137.
 *
 * The footer line reports **`totalPassing`, not `candidates.length`**. The oracle prints the full
 * count there while showing ten rows, and a UI that reported the row count would tell an engineer
 * that 10 sections work when 214 do — which changes what they conclude about how much margin the
 * design has.
 */
export function tableHTML(response: OptimiseResponse): string {
  if (response.candidates.length === 0) {
    return `<p class="vide">${EMPTY_SWEEP_MESSAGE}</p>`;
  }

  let h =
    '<table class="opt"><thead><tr><th>Section</th><th>Masse</th><th>Taux ELU</th>' +
    '<th>Taux ELS</th><th></th></tr></thead><tbody>';

  response.candidates.forEach((candidate, i) => {
    // Row 0 carries `class="best"`. It is the lightest section that passes, which is the answer
    // the user pressed the button for.
    h +=
      `<tr${i === 0 ? ' class="best"' : ''}><td>${candidate.nom}</td>` +
      `<td class="num">${fmt(candidate.poids, 1)} kg/m</td>` +
      `<td class="num">${ratio3(candidate.elu)}</td>` +
      `<td class="num">${ratio3(candidate.els ?? Number.NaN)}</td>` +
      `<td><button type="button" class="mini" data-pick="${candidate.nom}">appliquer</button></td></tr>`;
  });

  h +=
    `</tbody></table><p class="hint">Classement par masse croissante. ${response.totalPassing} ` +
    'section(s) satisfont ELU et ELS.</p>';

  return h;
}

/**
 * What "appliquer" writes back into the form.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * A KNOWN DISCREPANCY, PRESERVED AND DOCUMENTED RATHER THAN FIXED
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * For timber this returns `classe`, `b` and `h` — and **not** the rescaled `winstG` / `winstQ` the
 * candidate was actually ranked with. The sweep scales the entered deflections by the ratio of
 * bending stiffnesses (oracle line 1113, ported in US-109) so that each candidate is judged on its
 * own stiffness; "appliquer" does not write those scaled values back, so recalculating after
 * applying a candidate gives a **different ELS ratio** from the one shown in the table.
 *
 * That is inherited behaviour, and P1 does not fix it: correcting it would change an answer, and
 * the whole phase is refereed by answers not changing. `OPTIMISE.md` records it too.
 *
 * TODO(P2): decide whether "appliquer" should carry the rescaled deflections. It is an engine-
 * adjacent change and needs the engineer's sign-off, not a UI decision.
 *
 * The three fields come from the response as **structured values**. The oracle recovered them by
 * `nom.split(' ')` at indices 0, 1 and 3 — position-indexed parsing of a display label, which the
 * contract exists to remove.
 */
export function pickPatch(
  mod: FicheModule,
  response: OptimiseResponse,
  nom: string,
): Record<string, string | number> | undefined {
  const candidate = response.candidates.find((c) => c.nom === nom);
  if (candidate === undefined) return undefined;

  if (mod === 'lamine') return { profil: candidate.nom };
  if (mod === 'creux') return { section: candidate.nom };

  if (response.module !== 'bois') return undefined;
  const bois = response.candidates.find((c) => c.nom === nom);
  if (bois === undefined) return undefined;
  return { classe: bois.classe, b: bois.b, h: bois.h };
}

export interface OptimisePanelOptions {
  /** Called with the field patch when the user applies a candidate. */
  readonly onApply: (mod: FicheModule, patch: Record<string, string | number>) => void;
  /** Current form values for the module. */
  readonly readInput: (mod: FicheModule) => Record<string, unknown>;
}

/**
 * Wire the panel for one module.
 *
 * The pending state is not decoration. The oracle gave no feedback because the sweep blocked the
 * main thread and the page was frozen anyway; here the sweep is 2 811 engine calls happening on
 * another continent, and a button that looks idle invites a second click and a second charge.
 */
export function optimisePanel(mod: FicheModule, options: OptimisePanelOptions) {
  const box = must(`#opt_${mod}`);
  let current: OptimiseResponse | undefined;

  async function run(): Promise<ApiResult<OptimiseResponse>> {
    box.hidden = false;
    box.innerHTML = '<p class="vide">Recherche en cours...</p>';

    const input = options.readInput(mod);
    const body =
      mod === 'lamine'
        ? { module: 'lamine' as const, input, familles: selectedFamilies() }
        : mod === 'creux'
          ? { module: 'creux' as const, input }
          : { module: 'bois' as const, input, mode: selectedBoisMode() };

    const result = await optimiseRequest(body as never);

    if (!result.ok) {
      box.innerHTML = '<p class="vide">La recherche n a pas pu etre effectuee.</p>';
      return result;
    }

    current = result.value;
    box.innerHTML = tableHTML(result.value);

    for (const button of $$<HTMLButtonElement>('button[data-pick]', box)) {
      button.addEventListener('click', () => {
        const nom = button.dataset['pick'];
        if (nom === undefined || current === undefined) return;
        const patch = pickPatch(mod, current, nom);
        if (patch !== undefined) options.onApply(mod, patch);
      });
    }

    return result;
  }

  return { run };
}
